import { entityPaths } from '@lakaran/geometry';
import type { NodeGraph } from '@lakaran/nodes';
import type { CadEditor } from '../src/editor/editor';
import {
  createPiecePicker,
  type PickPiece,
  type PiecePicker,
} from './piece-picker';

/**
 * Finder: the drawing → node direction.
 *
 * Armed from the node canvas, every entity of the node preview is outlined; clicking one reports the
 * step that created it (`entity.origin`), which is then selected and revealed in the node editor.
 * It is a one-shot pick: after a hit the mode ends and the card does the talking.
 */

export interface FindPickerOptions {
  graph: () => NodeGraph;
  /** Called with the node behind the clicked shape. */
  onFind: (nodeId: string) => void;
  onEnd?: () => void;
}

export type FindPicker = PiecePicker;

export function createFindPicker(
  editor: CadEditor,
  host: HTMLElement,
  options: FindPickerOptions,
): FindPicker {
  const pieces = (): PickPiece[] =>
    editor.preview.map((entity, index) => ({
      // The key is the step that made it; duplicates (a plate cut in three) all point at one card.
      key: entity.source ?? `?${index}`,
      label: entity.name ?? entity.type,
      on: false,
      paths: entityPaths(entity),
    }));

  const picker = createPiecePicker(editor, host, {
    pieces,
    onToggle: (_nodeId, key) => {
      const nodeId = key.startsWith('?') ? '' : key;
      // One shot: the answer is a card to look at, not a selection to build up.
      picker.end();
      if (nodeId) options.onFind(nodeId);
      else editor.message('FIND  That shape came from a step this graph no longer has.');
    },
    hint: 'Finder — click a shape to jump to the node that made it, X or Esc ends',
    startMessage: (count) => `FIND  ${count} shapes in the preview — click one to find its node (X ends).`,
    emptyMessage: 'FIND  Nothing to look through — the graph has not drawn anything yet.',
    action: () => 'click to find its node',
    onClass: 'is-found',
    onEnd: options.onEnd,
  });

  return picker;
}
